import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import ReactDOM from 'react-dom'
import styled from 'styled-components'
import { Moment } from 'moment'
import { usePopper } from 'react-popper'

import TouchDeviceContext from 'contexts/TouchDeviceContext'

import DateRangeCalendar from './DateRangeCalendar'
import type { DateRangeCalendarProps } from './DateRangeCalendar'

export type FocusedInput = 'startDate' | 'endDate' | null

type ForwardedCalendarProps = Pick<
  DateRangeCalendarProps,
  | 'isDayBlocked'
  | 'isOutsideRange'
  | 'minDate'
  | 'maxDate'
  | 'minimumNights'
  | 'numberOfMonths'
  | 'onMonthChange'
  | 'renderDay'
  | 'daySize'
  | 'monthsGap'
  | 'firstDayOfWeek'
  | 'excludeDisabled'
>

export type DateRangePickerInputProps = ForwardedCalendarProps & {
  startDate: Moment | null
  endDate: Moment | null
  onDatesChange: (dates: {
    startDate: Moment | null
    endDate: Moment | null
  }) => void
  focusedInput: FocusedInput
  onFocusChange: (focused: FocusedInput) => void
  startDateId: string
  endDateId: string
  displayFormat: string
  // Defaults from TouchDeviceContext. When true, the calendar renders
  // as a full-screen portal'd modal; otherwise it anchors to the input
  // group via react-popper.
  withPortal?: boolean
  placeholderStart?: string
  placeholderEnd?: string
  className?: string
}

const InputsWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`

const DateInput = styled.input`
  flex: 1 1 0;
  min-width: 0;
  width: 100%;
  appearance: none;
  border: none;
  outline: none;
  background: transparent;
  font-size: 16px;
  padding: 16px;
  text-align: center;
  cursor: pointer;
  color: ${({ theme }) => theme['-ds-neutral-700'].value};

  &::placeholder {
    color: ${({ theme }) => theme['-ds-neutral-500'].value};
  }

  /* The popover opening + the brand value the user just picked are
     plenty of focus feedback; rdp's loud teal outline on every input
     transition reads as a layout glitch on this surface. */
  &:focus {
    outline: none;
  }
`

const InputSeparator = styled.span`
  flex: 0 0 auto;
  color: ${({ theme }) => theme['-ds-neutral-500'].value};
  padding: 0 4px;
`

// Suppress rdp's default --rdp-range_start-background / --rdp-range_end-background
// half-gradient on the start/end TDs and paint the whole selected span — plus
// the hover-preview span between a picked start and the currently-hovered
// day — with one continuous range_middle background. Without the TD-level
// paint, the wrapper's button-level fill renders as separate chips with
// gaps between cells. The hover-state override on preview cells stops
// rdp's `.rdp-day_button:hover` tint from re-introducing a per-cell box.
const rangeReset = `
  .rdp-selected,
  .rdp-range_start,
  .rdp-range_end,
  .rdp-preview_middle {
    background: var(--rdp-range_middle-background-color);
  }

  .rdp-preview_middle .rdp-day_button:not([aria-disabled='true']):hover {
    background: transparent;
    border-color: transparent;
  }
`

const Popover = styled.div`
  z-index: 5;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.12);
  padding: 16px;
  ${rangeReset}
`

const PortalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 50;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
`

const PortalSurface = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 16px;
  max-width: 100vw;
  max-height: 100vh;
  overflow: auto;
  ${rangeReset}
`

const DateRangePickerInput = ({
  startDate,
  endDate,
  onDatesChange,
  focusedInput,
  onFocusChange,
  startDateId,
  endDateId,
  displayFormat,
  withPortal: withPortalProp,
  placeholderStart = 'Start date',
  placeholderEnd = 'End date',
  className,
  // Forwarded to the inner calendar:
  isDayBlocked,
  isOutsideRange,
  minDate,
  maxDate,
  minimumNights,
  numberOfMonths = 1,
  onMonthChange,
  renderDay,
  daySize,
  monthsGap,
  firstDayOfWeek,
  excludeDisabled,
}: DateRangePickerInputProps) => {
  const isTouchDevice = useContext(TouchDeviceContext)
  const withPortal = withPortalProp ?? isTouchDevice
  const isOpen = focusedInput != null

  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(
    null
  )
  const [popoverElement, setPopoverElement] = useState<HTMLElement | null>(null)
  const startInputRef = useRef<HTMLInputElement>(null)
  const endInputRef = useRef<HTMLInputElement>(null)

  const popperModifiers = useMemo(
    () => [
      { name: 'offset', options: { offset: [0, 8] } },
      {
        name: 'preventOverflow',
        options: { padding: 16, boundary: 'clippingParents' },
      },
      {
        name: 'flip',
        options: { allowedAutoPlacements: ['top-start', 'bottom-start'] },
      },
    ],
    []
  )

  const { styles, attributes } = usePopper(referenceElement, popoverElement, {
    placement: 'bottom-start',
    modifiers: popperModifiers,
  })

  const handleOpenStart = useCallback(() => {
    onFocusChange('startDate')
  }, [onFocusChange])

  const handleOpenEnd = useCallback(() => {
    onFocusChange('endDate')
  }, [onFocusChange])

  const handleChange = useCallback(
    (newStart: Moment | null, newEnd: Moment | null) => {
      onDatesChange({ startDate: newStart, endDate: newEnd })
      // After committing a complete range, blur and close the popover.
      // On a partial selection (start only), keep the popover open and
      // advance focus to the end-date input — regardless of which input
      // opened the popover, the user's next meaningful action is the
      // end date.
      if (newStart && newEnd) {
        onFocusChange(null)
      } else if (newStart && !newEnd) {
        onFocusChange('endDate')
      }
    },
    [onDatesChange, onFocusChange]
  )

  // Close on outside-click. We listen on `mousedown` (capture) rather
  // than `click` so the popover commits to closing before any
  // descendant click handler runs — matters on iOS, where focus is
  // committed at mousedown and a deferred click can race the
  // popover's own input-focusing effects. ListingCalendar's outside
  // handler listens on `click`; the popover wears
  // `js-prevent-global-events` so the two listeners don't race when a
  // user clicks inside the popover.
  useEffect(() => {
    if (!isOpen) return undefined
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (referenceElement?.contains(target)) return
      if (popoverElement?.contains(target)) return
      onFocusChange(null)
    }
    document.addEventListener('mousedown', handler, { capture: true })
    return () => {
      document.removeEventListener('mousedown', handler, { capture: true })
    }
  }, [isOpen, referenceElement, popoverElement, onFocusChange])

  // Close on Escape. keydown so the popover closes the moment the key
  // is hit; keyup would wait for release and miss preventDefault races.
  useEffect(() => {
    if (!isOpen) return undefined
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onFocusChange(null)
      }
    }
    document.addEventListener('keydown', handler, { capture: true })
    return () => {
      document.removeEventListener('keydown', handler, { capture: true })
    }
  }, [isOpen, onFocusChange])

  // Lock body scroll while the portal'd modal is open so touch users
  // can't drag the page behind it.
  useEffect(() => {
    if (!isOpen || !withPortal) return undefined
    const { body } = document
    const previous = body.style.overflow
    body.style.overflow = 'hidden'
    return () => {
      body.style.overflow = previous
    }
  }, [isOpen, withPortal])

  // When the popover opens, focus the matching input so keyboard users
  // get a stable focus anchor and screen readers announce the trigger.
  useEffect(() => {
    if (focusedInput === 'startDate' && startInputRef.current) {
      startInputRef.current.focus()
    } else if (focusedInput === 'endDate' && endInputRef.current) {
      endInputRef.current.focus()
    }
  }, [focusedInput])

  // Note: rdp's defaultMonth only applies at mount, and the popover
  // unmounts on close via the `isOpen` guard below — so `focusedInput`
  // is read only at mount time even though it appears in the
  // computation. We deliberately omit it from the deps because tracking
  // it would imply a re-evaluation that never re-applies.
  const defaultMonth = useMemo(() => {
    if (focusedInput === 'endDate' && endDate) return endDate
    if (startDate) return startDate
    if (endDate) return endDate
    return undefined
  }, [startDate, endDate])

  const startValue = startDate ? startDate.format(displayFormat) : ''
  const endValue = endDate ? endDate.format(displayFormat) : ''

  const calendar = (
    <DateRangeCalendar
      startDate={startDate}
      endDate={endDate}
      onChange={handleChange}
      isDayBlocked={isDayBlocked}
      isOutsideRange={isOutsideRange}
      minDate={minDate}
      maxDate={maxDate}
      minimumNights={minimumNights}
      numberOfMonths={numberOfMonths}
      onMonthChange={onMonthChange}
      renderDay={renderDay}
      daySize={daySize}
      monthsGap={monthsGap}
      firstDayOfWeek={firstDayOfWeek}
      excludeDisabled={excludeDisabled}
      defaultMonth={defaultMonth}
    />
  )

  return (
    <>
      <InputsWrapper
        ref={setReferenceElement}
        className={className}
        role="group"
      >
        <DateInput
          ref={startInputRef}
          id={startDateId}
          name={startDateId}
          type="text"
          readOnly
          aria-label="Start date"
          aria-expanded={isOpen}
          aria-controls={isOpen ? `${startDateId}-calendar` : undefined}
          placeholder={placeholderStart}
          value={startValue}
          onFocus={handleOpenStart}
          onClick={handleOpenStart}
        />
        <InputSeparator aria-hidden>→</InputSeparator>
        <DateInput
          ref={endInputRef}
          id={endDateId}
          name={endDateId}
          type="text"
          readOnly
          aria-label="End date"
          aria-expanded={isOpen}
          aria-controls={isOpen ? `${endDateId}-calendar` : undefined}
          placeholder={placeholderEnd}
          value={endValue}
          onFocus={handleOpenEnd}
          onClick={handleOpenEnd}
        />
      </InputsWrapper>

      {isOpen &&
        withPortal &&
        ReactDOM.createPortal(
          // The same className the rest of the app uses to opt out of
          // ListingCalendar's outside-click selection-clear handler.
          // Backdrop clicks are caught by the outside-mousedown effect
          // above (PortalSurface contains the click target check), so
          // no inline handler is needed here.
          <PortalOverlay className="js-prevent-global-events">
            <PortalSurface
              ref={setPopoverElement}
              id={`${startDateId}-calendar`}
              role="dialog"
              aria-modal="true"
              aria-label="Pick a date range"
            >
              {calendar}
            </PortalSurface>
          </PortalOverlay>,
          document.body
        )}

      {isOpen && !withPortal && (
        <Popover
          ref={setPopoverElement}
          id={`${startDateId}-calendar`}
          className="js-prevent-global-events"
          role="dialog"
          aria-label="Pick a date range"
          style={styles.popper}
          {...attributes.popper}
        >
          {calendar}
        </Popover>
      )}
    </>
  )
}

export default DateRangePickerInput
